import { Link } from "react-router-dom";
import { useBrand } from "@branding/useBrand";
import { AppCta } from "../MarketingLayout";
import { ProductPreview } from "../ProductPreview";

/** Home / landing page: value proposition, capabilities, preview, proof, CTA. */
export function Landing() {
  const brand = useBrand();

  const capabilities = [
    {
      icon: "⚖",
      title: "Always tie-out correct",
      body: "Every journal entry flows into a live trial balance, P&L, and balance sheet that reconcile in real time. No month-end scramble to make the numbers agree.",
    },
    {
      icon: "🏛",
      title: "UAE tax built in",
      body: "5% VAT tracking on every transaction and corporate-tax (9%) categorization tags — attached as you post, not bolted on at filing time.",
    },
    {
      icon: "◆",
      title: "Multi-entity & consolidation",
      body: "Run each Free Zone or Mainland entity on its own books, then flip to a consolidated group view with a single switch.",
    },
    {
      icon: "$",
      title: "AED base + multi-currency",
      body: "Post foreign-currency invoices and bills with automatic FX gain/loss, all reporting back to your AED base.",
    },
    {
      icon: "⇌",
      title: "Bank reconciliation",
      body: "Match statement lines to ledger entries and clear the difference to zero — so the books you trust match the bank.",
    },
    {
      icon: "⇄",
      title: "Import / export to coexist",
      body: "CSV import and export let you run alongside your current tool instead of migrating on day one.",
    },
  ];

  return (
    <>
      {/* Hero */}
      <section className="mk-hero">
        <div className="mk-hero-grid">
          <div>
            <div className="mk-eyebrow">For UAE multi-entity finance teams</div>
            <h1 className="mk-h1">
              Books that always tie out — VAT, corporate tax, and multi-entity,
              baked in.
            </h1>
            <p className="mk-lead">
              {brand.name} is the double-entry ledger for UAE finance teams that
              produces a live, reconciling trial balance and financial statements
              from the transactions you enter — with 5% VAT, corporate-tax
              categorization, and AED-base multi-currency built in, not bolted on.
            </p>
            <div className="mk-hero-cta">
              <AppCta className="mk-btn primary lg">Start your free trial</AppCta>
              <Link className="mk-btn secondary lg" to="/features">
                Explore features
              </Link>
            </div>
            <p className="mk-hero-note">
              14-day free trial · No card required · Your ledger is stored securely
              and scoped to your organization.
            </p>
          </div>
          <ProductPreview />
        </div>
      </section>

      {/* Capabilities */}
      <section className="mk-section">
        <div className="mk-eyebrow">Core capabilities</div>
        <h2 className="mk-h2">Everything a UAE team needs to close with confidence</h2>
        <div className="mk-grid cols-3">
          {capabilities.map((c) => (
            <div className="mk-card" key={c.title}>
              <div className="mk-ico" aria-hidden="true">
                {c.icon}
              </div>
              <h3>{c.title}</h3>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Social proof */}
      <section className="mk-band">
        <div className="mk-section">
          <div className="mk-stats">
            <div className="mk-stat">
              <div className="n">Dr = Cr</div>
              <div className="l">Enforced on every post — the ledger cannot drift</div>
            </div>
            <div className="mk-stat">
              <div className="n">5% · 9%</div>
              <div className="l">VAT and corporate-tax aware from day one</div>
            </div>
            <div className="mk-stat">
              <div className="n">1–4</div>
              <div className="l">Built for the small finance team that keeps its own books</div>
            </div>
          </div>

          <div className="mk-quotes">
            <figure className="mk-quote">
              <blockquote>
                “We stopped rebuilding the trial balance in a spreadsheet every
                month. It just ties out — and the VAT is already sitting on each
                line.”
              </blockquote>
              <figcaption className="who">
                <span className="av" aria-hidden="true">AR</span>
                <span className="m">
                  <span className="name">Aisha Rahman</span>
                  <span className="role"> · Finance Manager, Free Zone trading group</span>
                </span>
              </figcaption>
            </figure>
            <figure className="mk-quote">
              <blockquote>
                “Consolidating three entities used to take a day. Now it’s a
                dropdown, and the FX gain/loss is already handled.”
              </blockquote>
              <figcaption className="who">
                <span className="av" aria-hidden="true">KM</span>
                <span className="m">
                  <span className="name">Karim Mansour</span>
                  <span className="role"> · Owner, Mainland SME</span>
                </span>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mk-cta">
        <div className="mk-section narrow">
          <h2 className="mk-h2">Start closing your books with numbers you trust.</h2>
          <p className="mk-lead" style={{ marginInline: "auto" }}>
            Create your organization in minutes and see a tied-out trial balance
            from your first entries.
          </p>
          <div className="mk-hero-cta">
            <AppCta className="mk-btn primary lg">Start your free trial</AppCta>
            <Link className="mk-btn secondary lg" to="/pricing">
              See pricing
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
